import React, { useMemo, useCallback, useRef, useEffect, useContext } from "react";
import { InputNumber, Space } from "antd";
import type { InputNumberProps as AntInputNumberProps } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import { useAndStore } from "../../base/store/useAndStore";
import { getDataSetStoreKey } from "../../base/storeKeyUtils";
import { parseChangeAction, createCommand } from "../../base/actionParser";
import { useFieldValidation } from "../../base/useFieldValidation";
import { mergeStoreProps } from "../../base/storePropsUtils";
import { ValidateHelp } from "../../base/ValidateHelp";
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from "../../types/common";
import { useResolvedDataSet, DataSetInheritContext } from "../../base/DataSetInheritContext";
import { resolveAffixRenderConfig, SPACE_ADDON_NOWRAP_STYLE } from "../../utils/inputAffixUtils";
import { prepareAntdDomProps } from "../../utils/normalizeReactStyle";
import clsx from "clsx";

const VALIDATE_DEBOUNCE_MS = 200;

export interface AndInputNumberProps
  extends Omit<
      AntInputNumberProps,
      "onChange" | "prefix" | "suffix" | "addonBefore" | "addonAfter"
    >,
    BaseComponentProps,
    DataSetConfig,
    ValidationDisplayConfig {
  /** Ant Design Icons 组件名 */
  prefix?: string;
  suffix?: string;
  addonBefore?: string;
  addonAfter?: string;
  rules?: Array<{ type?: string; required?: boolean; message?: string; min?: number; max?: number }>;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  changeAction?: string;
  pressEnterAction?: string;
  clearAction?: string;
  /** 显示清除按钮（antd InputNumber 无内置 allowClear，由组件用 suffix 实现） */
  allowClear?: boolean;
  stepAction?: string;
  onChange?: (value: number | null) => void;
}

export const AndInputNumber: React.FC<AndInputNumberProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  changeAction,
  pressEnterAction,
  clearAction,
  allowClear,
  stepAction,
  onChange,
  defaultValue,
  value: propValue,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;
  const ctx = useContext(DataSetInheritContext);

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const storeProps = useAndStore((s) =>
    effectiveSessionId && id ? s.propsStore[`${effectiveSessionId}_props`]?.[id] ?? null : null
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField ? s.getDataSet(dataSetKey, effectiveDataSetField) : undefined
  );

  const { validate, help } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  const displayValue = useMemo(() => {
    if (storeValue !== undefined) return storeValue;
    if (storeProps?.value !== undefined) return storeProps.value;
    return propValue;
  }, [storeValue, storeProps?.value, propValue]);

  const handleAction = useCallback(
    (actionConfig: string | undefined, actionType: string, value: any) => {
      if (actionConfig && sessionId) {
        addCommand(sessionId, createCommand(actionConfig, { type: actionType, value }));
      }
    },
    [sessionId, addCommand]
  );

  const handleChange = useCallback(
    (value: number | null) => {
      if (onChange) {
        onChange(value);
        return;
      }
      if (dataSetKey && effectiveDataSetField) setDataSet(dataSetKey, effectiveDataSetField, value);
      if (validateOnChange) {
        if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
        validateTimerRef.current = setTimeout(() => {
          validateTimerRef.current = null;
          validate(value);
        }, VALIDATE_DEBOUNCE_MS);
      }
      handleAction(changeAction, "change", value);
    },
    [
      onChange,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      validateOnChange,
      validate,
      changeAction,
      handleAction,
    ]
  );

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  const handlePressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (restProps.onPressEnter) {
        restProps.onPressEnter(e);
        return;
      }
      if (pressEnterAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(pressEnterAction, displayValue, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      restProps.onPressEnter,
      pressEnterAction,
      effectiveSessionId,
      displayValue,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      addCommand,
      ctx,
    ]
  );

  const handleClearClick = useCallback(() => {
    if (onChange) {
      onChange(null);
      return;
    }
    if (dataSetKey && effectiveDataSetField) setDataSet(dataSetKey, effectiveDataSetField, null);
    if (validateOnChange) {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
      validateTimerRef.current = setTimeout(() => {
        validateTimerRef.current = null;
        validate(null);
      }, VALIDATE_DEBOUNCE_MS);
    }
    handleAction(changeAction, "change", null);
    if (clearAction && effectiveSessionId) {
      const { actionName, arg } = parseChangeAction(clearAction, null, {
        id,
        dataSet: effectiveDataSet,
        dataSetField: effectiveDataSetField,
      }, ctx);
      addCommand(effectiveSessionId, createCommand(actionName, arg));
    }
  }, [
    onChange,
    dataSetKey,
    effectiveDataSetField,
    setDataSet,
    validateOnChange,
    validate,
    handleAction,
    changeAction,
    clearAction,
    effectiveSessionId,
    id,
    effectiveDataSet,
    effectiveDataSetField,
    addCommand,
    ctx,
  ]);

  const handleStep = useCallback(
    (value: number, info: { offset: number | string; type: "up" | "down" }) => {
      handleAction(stepAction, "step", { value, type: info.type });
    },
    [stepAction, handleAction]
  );

  useEffect(
    () => () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (
      defaultValue !== undefined &&
      displayValue === undefined &&
      dataSetKey &&
      effectiveDataSetField
    ) {
      setDataSet(dataSetKey, effectiveDataSetField, defaultValue);
    }
  }, [defaultValue, displayValue, dataSetKey, effectiveDataSetField, setDataSet]);

  const mergedRestProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, any>, storeProps ?? undefined),
    [restProps, storeProps]
  );
  const preparedDomProps = useMemo(
    () => prepareAntdDomProps(mergedRestProps as Record<string, unknown>),
    [mergedRestProps],
  );
  const {
    prefix: prefixRaw,
    suffix: suffixRaw,
    addonBefore,
    addonAfter,
    style,
    ...otherProps
  } = preparedDomProps;

  const prefixConfig = useMemo(
    () =>
      resolveAffixRenderConfig({
        iconName:
          prefixRaw !== undefined && prefixRaw !== null ? String(prefixRaw) : undefined,
        label:
          addonBefore !== undefined && addonBefore !== null
            ? String(addonBefore)
            : undefined,
      }),
    [prefixRaw, addonBefore]
  );
  const suffixConfig = useMemo(
    () =>
      resolveAffixRenderConfig({
        iconName:
          suffixRaw !== undefined && suffixRaw !== null ? String(suffixRaw) : undefined,
        label:
          addonAfter !== undefined && addonAfter !== null ? String(addonAfter) : undefined,
      }),
    [suffixRaw, addonAfter]
  );
  const prefixIconNode = prefixConfig.iconNode;
  const suffixIconNode = suffixConfig.iconNode;
  const prefixAddonText = prefixConfig.iconConfigured ? undefined : prefixConfig.labelText;
  const suffixAddonText = suffixConfig.iconConfigured ? undefined : suffixConfig.labelText;

  const mergedSuffix = useMemo(() => {
    const showClear =
      allowClear && displayValue !== null && displayValue !== undefined;
    if (!showClear) return suffixIconNode;
    const clearIcon = (
      <span
        role="button"
        aria-label="clear"
        style={{ cursor: "pointer", marginInlineStart: 4 }}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          handleClearClick();
        }}
      >
        <CloseCircleOutlined />
      </span>
    );
    if (suffixIconNode == null || suffixIconNode === false) return clearIcon;
    return (
      <>
        {suffixIconNode}
        {clearIcon}
      </>
    );
  }, [allowClear, displayValue, suffixIconNode, handleClearClick]);

  return (
    <div style={{ width: "100%" }}>
      <Space.Compact style={{ width: "100%" }}>
        {prefixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{prefixAddonText}</Space.Addon> : null}
        <InputNumber
          {...(otherProps as Record<string, unknown>)}
          disabled={develop ? false : (otherProps.disabled as boolean | undefined)}
          readOnly={develop ? true : (otherProps.readOnly as boolean | undefined)}
          className={clsx(otherProps.className as string | undefined, {
            "ant-input-number-disabled": develop && otherProps.disabled,
          })}
          prefix={prefixIconNode}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onPressEnter={handlePressEnter}
          onStep={handleStep}
          suffix={mergedSuffix}
          status={help ? ("error" as const) : undefined}
          style={{ width: "100%", ...(style as React.CSSProperties) }}
        />
        {suffixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{suffixAddonText}</Space.Addon> : null}
      </Space.Compact>
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndInputNumber;
